import React, { useContext } from 'react'
import { useEffect, useState } from 'react'
import { Button, Col, Container, Inline, Modal, Row } from '@airbus/components-react/components'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import ModalSn from 'components/Modal/modalSn'
import PageHeader from 'components/pageHeader'
import { DisplayModus, PN_TYPES } from 'constants/constants'
import useMutation from 'utils/hooks/useMutation'
import URL_APIS from 'utils/api/urlApis'
import { UsedToolInput } from 'types/workorder/usedTool'
import ModalConfirm from 'components/Modal/ModalConfim'
import { FormProvider, useForm } from 'react-hook-form'
import FormSelect from 'components/FormFields/FormSelect'
import useOptions from 'utils/hooks/useOptions'
import FormInput from 'components/FormFields/FormInput'
import ModalPn from 'components/Modal/modalPn'
import { PnSearch, SnSearch } from 'types/pn'
import { Lang } from 'context/LangContext'
import FormDate from 'components/FormFields/FormDate'
import { format, isValid } from 'date-fns'
import urlApis from 'utils/api/urlApis'
import useQuery from 'utils/hooks/useQuery'
import LightDarkContext from 'context/LightDarkModeContext'

type Props = {
  lang: Lang
  open: boolean
  displayMode: DisplayModus.CREATE | DisplayModus.WRITE
  onClose: () => void
  usedTool?: UsedToolInput
  onPostSubmit: () => void
}

const styleModal = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  border: '2px solid #000',
  boxShadow: 24,
  height: '500px',
  width: '60%',
  overflow: 'hidden',
}

const ModalUsedTools = ({ lang, open, displayMode, onClose, usedTool, onPostSubmit }: Props) => {
  const { t } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [modalPnOpened, setModalPnOpened] = useState(false)
  const [modalSnOpened, setModalSnOpened] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { fetchData: fetchPn } = useQuery<PnSearch>(urlApis.URL_COMMON_PN_GET)
  const { mutate: createOrUpdateUsedTools } = useMutation<UsedToolInput>(URL_APIS.URL_WORKORDER_USED_TOOLS_POST)

  const { loadOptions, options: typeOptions } = useOptions(URL_APIS.URL_WORKORDER_REQUIRED_TOOLS_INGREDIENT_TYPES_GET)

  const methods = useForm<UsedToolInput>({
    values: usedTool,
  })

  const {
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    resetField,
    watch,
  } = methods

  const selectedPn = watch('pn')
  const selectedPnTypeCode = watch('pnTypeCode')

  useEffect(() => {
    loadOptions()
  }, [])

  const onSubmit = async (data: UsedToolInput) => {
    setIsLoading(true)

    await createOrUpdateUsedTools({
      data: {
        ...usedTool,
        ...data,
        echeance: data.echeance && format(data.echeance, 'dd/MM/yyyy'),
      },
      params: { lang },
      method: displayMode === DisplayModus.CREATE ? 'POST' : 'PUT',
    })
    reset()
    onPostSubmit()
    setIsLoading(false)
  }

  const handleClose = () => {
    setConfirmOpen(false)
    reset()
    onClose()
  }

  const loadPn = async (pn: string) => {
    setValue('pnTypeCode', '')
    const pnResponse = await fetchPn({ lang: lang, pn: pn })
    setValue('pnTypeCode', pnResponse?.data?.pnTypeCode)
    setValue('designation', pnResponse?.data?.designation)
  }

  const handlePnChanged = (val: string) => {
    resetField('sn')
    loadPn(val)
  }

  const handlePnSelected = (pn: PnSearch) => {
    setModalPnOpened(false)
    setValue('pn', pn?.pn)
    setValue('pnTypeCode', pn?.pnTypeCode)
    resetField('sn')
    setValue('designation', pn?.designation)
  }

  const handleSnSelected = (sn: SnSearch) => {
    setModalSnOpened(false)
    setValue('sn', sn?.sn)
  }

  const isEmptyOrValid = (fieldDate: Date) => {
    if (!fieldDate) {
      return true
    }
    return isValid(fieldDate)
  }

  const { getBackgroundColor } = useContext(LightDarkContext)

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <div style={{ ...styleModal, backgroundColor: getBackgroundColor() }}>
          <PageHeader
            lang={lang}
            pageTitle={t('9300198')}
            modalEmbedded={true}
            modalOnClose={onClose}
            style={{ top: 0 }}
            localLoading={isLoading}
          />
          <Container style={{ flex: 1 }} fixed>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div style={{ margin: '20px 50px' }}>
                  <Row justify='space-between'>
                    <Col md={5} xs={12}>
                      <FormSelect
                        name='codeType'
                        label='code type'
                        rules={{ required: true }}
                        options={typeOptions}
                        error={Boolean(errors?.codeType)}
                      />
                    </Col>
                    <Col md={5} xs={12}>
                      <FormInput
                        name='pn'
                        label={
                          <>
                            {t('9500047')}
                            <FontAwesomeIcon
                              icon={faExternalLinkAlt}
                              className='color_primary'
                              style={{ marginLeft: '1ex' }}
                              onClick={() => setModalPnOpened(true)}
                            />
                          </>
                        }
                        maxLength={40}
                        capitalize
                        error={Boolean(errors.pn)}
                        rules={{ required: true }}
                        fullWidth
                        onChange={handlePnChanged}
                      />
                    </Col>
                  </Row>
                  <Row justify='space-between'>
                    <Col md={5} xs={12}>
                      <FormInput
                        name='sn'
                        label={
                          <>
                            {t('9500048')}
                            <FontAwesomeIcon
                              icon={faExternalLinkAlt}
                              className={
                                selectedPn &&
                                [
                                  PN_TYPES.CONSOMMABLE_BATCH_NUMBER,
                                  PN_TYPES.SERIALISE_REPARABLE,
                                  PN_TYPES.SERIALISE_NON_REPARABLE,
                                ].includes(selectedPnTypeCode)
                                  ? 'color_primary'
                                  : 'color_disabled'
                              }
                              style={{ marginLeft: '1ex' }}
                              onClick={() => (selectedPn ? setModalSnOpened(true) : null)}
                            />
                          </>
                        }
                        disabled={
                          !selectedPn ||
                          ![
                            PN_TYPES.CONSOMMABLE_BATCH_NUMBER,
                            PN_TYPES.SERIALISE_REPARABLE,
                            PN_TYPES.SERIALISE_NON_REPARABLE,
                          ].includes(selectedPnTypeCode)
                        }
                        rules={{
                          required:
                            selectedPn &&
                            [
                              PN_TYPES.CONSOMMABLE_BATCH_NUMBER,
                              PN_TYPES.SERIALISE_REPARABLE,
                              PN_TYPES.SERIALISE_NON_REPARABLE,
                            ].includes(selectedPnTypeCode),
                        }}
                        maxLength={15}
                        capitalize
                        error={Boolean(errors.sn)}
                        fullWidth
                      />
                    </Col>
                    <Col md={5} xs={12}>
                      <FormDate
                        label={t('1MOE030')}
                        name='echeance'
                        rules={{
                          validate: isEmptyOrValid,
                        }}
                        error={Boolean(errors?.echeance)}
                        fullWidth
                      />
                    </Col>
                  </Row>
                  <Row justify='space-between'>
                    <Col md={5} xs={12}>
                      <FormInput name='designation' label={t('7000248')} disabled fullWidth />
                    </Col>
                  </Row>
                </div>
                <Inline
                  align='space-around'
                  style={{
                    padding: '10px',
                    position: 'absolute',
                    bottom: 0,
                    marginTop: '1em',
                    width: '100%',
                  }}
                >
                  <Button onClick={() => setConfirmOpen(true)} variant='secondary'>
                    {t('8000569')}
                  </Button>
                  <Button type='submit' variant='primary' loading={isLoading || !isDirty}>
                    {t('9500174')}
                  </Button>
                </Inline>
              </form>
            </FormProvider>
          </Container>
        </div>
      </Modal>
      <ModalConfirm
        confirmMessage={t('GEN0090')}
        isOpened={confirmOpen}
        onCloseModal={() => setConfirmOpen(false)}
        onSubmit={handleClose}
      />
      <ModalPn
        formTitle={t('9500144')}
        lang={lang}
        isOpened={modalPnOpened}
        onCloseModal={() => setModalPnOpened(false)}
        onPnSelected={handlePnSelected}
        noPnFilter
      />
      <ModalSn
        formTitle={t('9500144')}
        isOpened={modalSnOpened}
        onCloseModal={() => setModalSnOpened(false)}
        pn={selectedPn}
        onSnSelected={handleSnSelected}
      />
    </>
  )
}

export default ModalUsedTools
